import moment from "moment";

export interface ScopeCertificateDetails {
  scopeCertificateNo: string; // Numeric field
  brandName: string[]; // Multi-select dropdown
  gotsSCNo: string; // Optional text field
  scLegacyNo: string[]; // Optional text field
  scCBAuditorId: string[]; // Dropdown
  scStandardProgram: string; // Dropdown
  scStatus: string; // Dropdown
  scWithdrawalReason: string; // Dropdown
  scSuspensionReason: string; // Dropdown
  scCBLicensingCode: string; // Text field
  scVersionNo: string; // Numeric field
  ccsFlag: string;
  // globalComment: string; // Text field
}

export interface StandardDetails {
  scStandard: string; // Text field
  scStandardDescription?: string;
  scStandardVersion: string; // Text field
  scNumberOfFarms: string; // Numeric field
  numberOfAuditDays: string; // Numeric field
  certifiedOrganizationApprovedClaims: string; // Optional text field
  certifiedOrganizationApprovedClaimsDescription: string;
}

export interface ScopeCertificateDates {
  scValidUntil: string; // Date in 'YYYY-MM-DD' format
  scDateOfIssue: string; // Date in 'YYYY-MM-DD' format
  scPlaceOfIssue: string; // Combined field: "State, Country"
  scPlaceOfIssueCountry?: string; // Country code for dropdown
  scPlaceOfIssueState?: string; // State code for dropdown
  scLastUpdated: string; // Date in 'YYYY-MM-DD' format
  scLastAuditDate: string; // Date in 'YYYY-MM-DD' format
  scTransferAuditDate: string; // Date in 'YYYY-MM-DD' format
  scExtendedUntil: string; // Date in 'YYYY-MM-DD' format
  scLastAuditStart: string; // Date in 'YYYY-MM-DD' format
}

export interface AccreditationDetails {
  scScopeStipulations: string; // Optional text field
  accreditationBody: string; // Dropdown
  accreditationNo: string; // Optional text field
  inspectionBody: string; // Optional text field
}

export interface ScopeCertificateForm {
  scopeCertificateDetails: ScopeCertificateDetails; // Main certificate details
  standardDetails: StandardDetails[]; // Array of standard details (table rows)
  scopeCertificateDates: ScopeCertificateDates; // Certificate date details
  accreditationDetails: AccreditationDetails; // Accreditation-related details
}


export function mapToScopeCertificateForm(response: any): ScopeCertificateForm {
  return {
    scopeCertificateDetails: {
      scopeCertificateNo: response.scNo || "", // Converting scId to number
      brandName: Array.isArray(response.brandName)
        ? response.brandName.filter((item: string) => item && item.trim().length > 0)
        : [], // Filter empty strings from brandName array
      gotsSCNo: response.gotsScNo || "", // Optional field
      scLegacyNo: Array.isArray(response.scLegacyNo)
        ? response.scLegacyNo.filter((item: string) => item && item.trim().length > 0)
        : [], // Filter empty strings from scLegacyNo array
      scCBAuditorId: Array.isArray(response.scCbAuditorId)
        ? response.scCbAuditorId.filter((item: string) => item && item.trim().length > 0)
        : [], // Filter empty strings from scCbAuditorId array
      scStandardProgram: response.scStandardProgram || "", // Dropdown value
      scStatus: response.scStatus || "", // Dropdown value
      scWithdrawalReason: response.scWithdrawalReason || "", // Dropdown value
      scSuspensionReason: response.scSuspensionReason || "", // Dropdown value
      scCBLicensingCode: response.scCbLicensingCode || "", // Text field
      scVersionNo: response.scVersionNo || "0", // Converting scVersionNo to number
      ccsFlag: response?.ccsFlag ?? ""
      // globalComment: response.globalCommentNotForActualDataToBeProvided || "", // Assuming a property for globalComment
    },
    standardDetails: response.scStandardDetails.length > 0 ? response.scStandardDetails.map((item: any) => {
      // Mapping for approved claims code to description
      const approvedClaimsMap: Record<string, string> = {
        "PC": "Product Claims (PC)",
        "NPC": "Non-Product Claims (NPC)",
        "PNPC": "Product and Non-Product Claims (PNPC)",
        "NC": "No Claims (NC)",
        "": ""
      };

      return {
        scStandard: item.scStandard,
        scStandardDescription: item.scStandardDescription,
        scStandardVersion: item.scStandardVersion,
        scNumberOfFarms: item.scNumberOfFarms,  // Map 'scNoOfFarms' to 'scNumberOfFarms'
        numberOfAuditDays: item.numberOfAuditDays,
        certifiedOrganizationApprovedClaims: item.certifiedOrganizationApprovedClaims,
        // If description is not provided, map from code
        certifiedOrganizationApprovedClaimsDescription:
          item.certifiedOrganizationApprovedClaimsDescription ||
          approvedClaimsMap[item.certifiedOrganizationApprovedClaims] ||
          "",
        actions: item.actions ?? ""
      };
    }) : [],
    scopeCertificateDates: {
      scValidUntil: response.scValidUntil || "",
      scDateOfIssue: response.scDateOfIssue || "",
      scPlaceOfIssue: response.scPlaceOfIssue || "",
      scPlaceOfIssueCountry: "",
      scPlaceOfIssueState: "",
      scLastUpdated: response.scLastUpdated || "",
      scLastAuditDate: response.scLastAuditDate || "",
      scTransferAuditDate: response.scTransferAuditDate || "",
      scExtendedUntil: response.scExtendedUntil || "",
      scLastAuditStart: response.scLastAuditStart || "",
    },
    accreditationDetails: {
      scScopeStipulations: response.scCbScopeStipulations || "",
      accreditationBody: response.scCbAccreditationBody || "",
      accreditationNo: response.scCbAccreditationNo || "",
      inspectionBody: response.scCbInspectionBody || "",
    },
  };
}

export function scopeCertificateFormToMap(scopeCertificateForm: ScopeCertificateForm, SC_ID?: string | undefined, cbLicenseCode?: any): any {
  const { scopeCertificateDetails, standardDetails, scopeCertificateDates, accreditationDetails } = scopeCertificateForm;
  const scStandardProgramOptions = [
    { label: "Responsible Animal Fiber", value: "RAS,RWS,RAS" },
    { label: "Content Claim Standard", value: "CCS" }
  ]
  const statusOptions: any = [
    { label: "Valid", value: "V" }, // Represents "Valid"
    { label: "Expired", value: "E" }, // Represents "Expired"
    { label: "Suspended", value: "S" }, // Represents "Suspended"
    { label: "Withdrawn", value: "W" }, // Represents "Withdrawn"
  ]
  const scStandardOptions: any = [
    { label: "Organic Content Standard", value: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard", value: "GRS" }, // Represents "Expired"
    { label: "Recycled Claim Standard", value: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard", value: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard", value: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard", value: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard", value: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard", value: "CCS" }, // Represents "Withdrawn"
    { label: "SFA", value: "SFA" }, // Represents "Withdrawn"
    { label: "Global Organic Textile Standard", value: "GOTS" }, // Represents "Withdrawn"
  ];
  const certifiedOrganizationApprovedClaimsOptions: any = [
    { label: "Product Claims", value: "PC" }, // Represents "Valid"
    { label: "Non-Product Claims", value: "NPC" }, // Represents "Expired"
    { label: "Product and Non-Product Claims", value: "PNPC" }, // Represents "Suspended"
    { label: "No Claims", value: "NC" }, // Represents "Withdrawn"
  ];
  const cbAccreditationBodyOptions: any = [
    { value: 'SLAB', label: 'SLAB' },
    { value: 'IOAS', label: 'IOAS' },
    { value: 'ANAB', label: 'ANAB' },
    { value: 'TAF', label: 'TAF' },
    { value: 'COFRAC', label: 'COFRAC' },
    { value: 'IAJapan', label: 'IA Japan' },
    { value: 'DAkkS', label: 'DAkkS' },
  ]
  const withdrawnReasons: any = [
    { label: "Critical non-conformity", value: "C" }, // Represents "Critical non-conformity"
    { label: "Non-conformity", value: "N" }, // Represents "Non-conformity"
    { label: "Recertification completed", value: "R" }, // Represents "Recertification completed"
    { label: "Withdrawal of the certification body’s accreditation", value: "W" }, // Represents "Withdrawal of accreditation"
    { label: "Administrative", value: "A" }, // Represents "Administrative"
    { label: "Voluntary", value: "V" }, // Represents "Voluntary"
    { label: "Other", value: "O" }, // Represents "Other"
  ];
  const suspensionReasons: any = [
    { label: "Critical non-conformity", value: "C" }, // Represents "Critical non-conformity"
    { label: "Non-conformity", value: "N" }, // Represents "Non-conformity"
    { label: "Administrative", value: "A" }, // Represents "Administrative"
    { label: "Other", value: "O" }, // Represents "Other"
  ];
  let normalizedScNo = scopeCertificateDetails.scopeCertificateNo || "";
  if (normalizedScNo.toLowerCase().startsWith("sc-")) {
    normalizedScNo = "SC-" + normalizedScNo.slice(3);
  }

  let normalizedScVersionNo = scopeCertificateDetails.scVersionNo ? scopeCertificateDetails.scVersionNo.toString() : "";
  if (normalizedScVersionNo.toLowerCase().startsWith("sc-")) {
    normalizedScVersionNo = "SC-" + normalizedScVersionNo.slice(3);
  }

  return {
    ...(SC_ID ? { scId: SC_ID } : {}),
    scNo: normalizedScNo, // Placeholder, as this is not present in ScopeCertificateForm
    brandName: scopeCertificateDetails.brandName || [], // Get the first item from the array
    gotsScNo: scopeCertificateDetails.gotsSCNo || "",
    scLegacyNo: scopeCertificateDetails.scLegacyNo || [],
    // globalCommentNotForActualDataToBeProvided: scopeCertificateDetails.globalComment || "",
    scCbAuditorId: scopeCertificateDetails.scCBAuditorId || [], // Wrap in an array
    scStandardDetails: standardDetails.length > 0 ? standardDetails.map((item: any) => ({
      scStandard: item.scStandard || "",
      scStandardDescription: scStandardOptions.find((option: any) => option.value === (item.scStandard ?? ""))?.label.trim() ?? "",
      scStandardVersion: item.scStandardVersion || "",
      scNumberOfFarms: item.scNumberOfFarms || 0,  // Map 'scNoOfFarms' to 'scNumberOfFarms'
      numberOfAuditDays: item.numberOfAuditDays || 0,
      certifiedOrganizationApprovedClaims: item.certifiedOrganizationApprovedClaims || "",
      certifiedOrganizationApprovedClaimsDescription: certifiedOrganizationApprovedClaimsOptions.find((option: any) => option.value === (item.certifiedOrganizationApprovedClaims ?? ""))?.label.trim() ?? "",
      actions: item.actions ?? ""
    })) : [],
    scStandardProgram: scopeCertificateDetails.scStandardProgram || "",
    ccsFlag: scopeCertificateDetails?.ccsFlag ?? "",
    scStandardProgramDescription: scStandardProgramOptions.find(Option => Option.value === (scopeCertificateDetails.scStandardProgram ?? ""))?.label.trim() ?? "",
    scStatus: scopeCertificateDetails.scStatus || "",
    scStatusDescription: statusOptions.find((Option: any) => Option.value === (scopeCertificateDetails.scStatus ?? ""))?.label.trim() ?? "",
    scWithdrawalReason: scopeCertificateDetails.scWithdrawalReason || "",
    scWithdrawalReasonDescription: withdrawnReasons.find((Option: any) => Option.value === (scopeCertificateDetails.scWithdrawalReason ?? ""))?.label.trim() ?? "",
    scSuspensionReason: scopeCertificateDetails.scSuspensionReason || "",
    scSuspensionReasonDescription: suspensionReasons.find((Option: any) => Option.value === (scopeCertificateDetails.scSuspensionReason ?? ""))?.label.trim() ?? "",
    scCbLicensingCode: cbLicenseCode,
    scVersionNo: normalizedScVersionNo, // Convert number to string
    scValidUntil: scopeCertificateDates.scValidUntil || "",
    scDateOfIssue: scopeCertificateDates.scDateOfIssue || "",
    scPlaceOfIssue: scopeCertificateDates.scPlaceOfIssue || "",
    scLastUpdated: scopeCertificateDates.scLastUpdated || "",
    scLastAuditDate: scopeCertificateDates.scLastAuditDate || "",
    scTransferAuditDate: scopeCertificateDates.scTransferAuditDate || "",
    scCbScopeStipulations: accreditationDetails.scScopeStipulations || "",
    scCbAccreditationBody: accreditationDetails.accreditationBody || "",
    scCbAccreditationBodyDescription: cbAccreditationBodyOptions.find((option: any) => option.value === (accreditationDetails.accreditationBody ?? ""))?.label.trim() ?? "",
    scCbAccreditationNo: accreditationDetails.accreditationNo || "",
    scCbInspectionBody: accreditationDetails.inspectionBody || "",
    scExtendedUntil: scopeCertificateDates.scExtendedUntil || "",
    scLastAuditStart: scopeCertificateDates.scLastAuditStart || ""
  };
}
